import { type ReactNode, Suspense } from "react";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import type { AppStore } from "~/app/store";
import { theme } from "~/app/theme";
import { GlobalStyles as MuiGlobalStyles, ThemeProvider, type Theme } from "~/common/mui";
import { queryClient } from "~/app/queryClient";
import { AppBootstrapProvider } from "~/app/providers/AppBootstrapProvider";
import { SessionProvider } from "~/app/providers/SessionProvider";
import { MinicartProvider } from "~/app/providers/MinicartProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { externalContentStyles } from "~/common/styles/ExternalContentGlobalStyles";
import { printStyles } from "~/common/styles/PrintGlobalStyles";
import { vendorAndIframeStyles } from "~/common/styles/VendorAndIframeGlobalStyles";
import type { ComponentProps } from "~/componentProps";
import { mountComponentRoot, unmountComponentRoot } from "~/utils/componentRootMount";

const globalStyles = (theme: Theme) => ({
  ...printStyles(theme),
  ...externalContentStyles(theme),
  ...vendorAndIframeStyles(theme),
});

window.SCHA = window.SCHA || {};
window.SCHA.cmps = {};

let store: AppStore | null = null;

export function initializeComponentRegistry(nextStore: AppStore): void {
  store = nextStore;
}

function renderInto(componentName: string, rootElement: HTMLElement, props: unknown) {
  const factory = window.SCHA.cmps[componentName];
  if (!factory) {
    console.error(`Could not find a component registered as ${componentName}`);
    return;
  }

  mountComponentRoot(renderRoot(factory(props), componentName), rootElement);
}

/**
 * Mounts a registered React island into `rootElement`.
 *
 * Migrated islands are typed via the global `ComponentProps` map; not-yet-migrated
 * ones fall through to the legacy overload. The JSP side calls this indirectly through
 * `window.SCHA.createComponentAtId(name, rootId, JSON.parse("${reactPageProps}"))`.
 */
export function createComponentInElement<K extends keyof ComponentProps>(
  componentName: K,
  rootElement: HTMLElement,
  props: ComponentProps[K],
): void;
export function createComponentInElement(componentName: string, rootElement: HTMLElement, props?: unknown): void;
export function createComponentInElement(componentName: string, rootElement: HTMLElement, props?: unknown) {
  renderInto(componentName, rootElement, props);
}

window.SCHA.createComponentInElement = createComponentInElement;
window.SCHA.unmountComponentAtNode = unmountComponentRoot;

/**
 * Registers a React island factory under the name a JSP mounts via
 * `createComponentAtId(name, rootId, JSON.parse("${reactPageProps}"))`.
 *
 * Prefer the typed overload (add the component to the `ComponentProps` map in
 * `src/componentProps.ts` first) so the factory's props are checked against the
 * contract. The legacy overload keeps not-yet-migrated islands compiling.
 *
 * Note: this registration is the hard enforcement point — a factory's props must
 * match `ComponentProps[K]`. The `createComponentInElement` / `mountIsland` call
 * sites only check props when called with a migrated name as a string literal; a
 * non-literal name falls through to the legacy overload and is not checked.
 */
export function registerComponentFactory<K extends keyof ComponentProps>(
  name: K,
  factory: (props: ComponentProps[K]) => ReactNode,
): void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function registerComponentFactory(name: string, factory: (props: any) => ReactNode): void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function registerComponentFactory(name: string, factory: (props: any) => ReactNode) {
  if (import.meta.env.DEV) {
    if (window.SCHA.cmps[name]) {
      console.warn(`Component registry: Overriding an already registered component at ${name}`);
    }
  }

  window.SCHA.cmps[name] = factory;
}

const renderRoot = (node: ReactNode, _componentName: string) => {
  if (!store) {
    throw new Error("Component registry has not been initialized with a Redux store");
  }

  return (
    <Suspense fallback={null}>
      <ThemeProvider theme={theme}>
        <MuiGlobalStyles styles={globalStyles} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <AppBootstrapProvider>
                <SessionProvider>
                  <MinicartProvider>{node}</MinicartProvider>
                </SessionProvider>
              </AppBootstrapProvider>
            </QueryClientProvider>
          </Provider>
        </LocalizationProvider>
      </ThemeProvider>
    </Suspense>
  );
};
